import {
  File as FileIcon,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileCode,
  FileVideo,
  FileAudio,
  FileArchive,
  type LucideIcon,
} from 'lucide-react';

interface Variant {
  Icon: LucideIcon;
  className: string;
}

const EXTENSION_MAP: Record<string, Variant> = {
  // Documents
  pdf: { Icon: FileText, className: 'text-red-600' },
  doc: { Icon: FileText, className: 'text-blue-700' },
  docx: { Icon: FileText, className: 'text-blue-700' },
  rtf: { Icon: FileText, className: 'text-blue-700' },
  odt: { Icon: FileText, className: 'text-blue-700' },
  txt: { Icon: FileText, className: 'text-slate-600' },
  md: { Icon: FileText, className: 'text-slate-600' },

  // Spreadsheets
  xls: { Icon: FileSpreadsheet, className: 'text-green-700' },
  xlsx: { Icon: FileSpreadsheet, className: 'text-green-700' },
  ods: { Icon: FileSpreadsheet, className: 'text-green-700' },
  csv: { Icon: FileSpreadsheet, className: 'text-green-700' },

  // Slides
  ppt: { Icon: FileText, className: 'text-orange-600' },
  pptx: { Icon: FileText, className: 'text-orange-600' },

  // Images
  png: { Icon: FileImage, className: 'text-purple-600' },
  jpg: { Icon: FileImage, className: 'text-purple-600' },
  jpeg: { Icon: FileImage, className: 'text-purple-600' },
  gif: { Icon: FileImage, className: 'text-purple-600' },
  webp: { Icon: FileImage, className: 'text-purple-600' },
  svg: { Icon: FileImage, className: 'text-purple-600' },
  heic: { Icon: FileImage, className: 'text-purple-600' },
  tif: { Icon: FileImage, className: 'text-purple-600' },
  tiff: { Icon: FileImage, className: 'text-purple-600' },

  // Archives
  zip: { Icon: FileArchive, className: 'text-amber-700' },
  tar: { Icon: FileArchive, className: 'text-amber-700' },
  gz: { Icon: FileArchive, className: 'text-amber-700' },
  '7z': { Icon: FileArchive, className: 'text-amber-700' },
  rar: { Icon: FileArchive, className: 'text-amber-700' },

  // Code / data
  json: { Icon: FileCode, className: 'text-slate-700' },
  xml: { Icon: FileCode, className: 'text-slate-700' },
  html: { Icon: FileCode, className: 'text-slate-700' },
  js: { Icon: FileCode, className: 'text-slate-700' },
  ts: { Icon: FileCode, className: 'text-slate-700' },
  py: { Icon: FileCode, className: 'text-slate-700' },

  // Media
  mp4: { Icon: FileVideo, className: 'text-pink-600' },
  mov: { Icon: FileVideo, className: 'text-pink-600' },
  avi: { Icon: FileVideo, className: 'text-pink-600' },
  webm: { Icon: FileVideo, className: 'text-pink-600' },
  mp3: { Icon: FileAudio, className: 'text-cyan-700' },
  wav: { Icon: FileAudio, className: 'text-cyan-700' },
  m4a: { Icon: FileAudio, className: 'text-cyan-700' },
};

const DEFAULT: Variant = { Icon: FileIcon, className: 'text-bb-lilac' };

export function getFileTypeVariant(filename: string): Variant {
  const dot = filename.lastIndexOf('.');
  if (dot < 0 || dot === filename.length - 1) return DEFAULT;
  const ext = filename.slice(dot + 1).toLowerCase();
  return EXTENSION_MAP[ext] ?? DEFAULT;
}

interface Props {
  name: string;
  size?: number;
  className?: string;
}

/** Renders a color-coded file icon based on the filename extension. */
export function FileTypeIcon({ name, size = 16, className }: Props) {
  const v = getFileTypeVariant(name);
  return <v.Icon size={size} className={className ?? v.className} />;
}
